import { motion } from "framer-motion";
import { Card } from "../../../components/ui/Card";
import {
  Target,
  LockSimple,
  RocketLaunch,
  ChartLineUp,
  ClipboardText,
  ArrowsClockwise,
  Brain,
  Globe,
} from "@phosphor-icons/react";

const features = [
  {
    title: "Quality Monitor",
    description:
      "Scale your code quality effortlessly with automated standards enforcement on every PR, perfect for growing engineering teams",
    icon: <Target size={32} weight="thin" className="group-hover:animate-subtle-float" />,
  },
  {
    title: "Security Scanner",
    description:
      "Stay secure as you scale with comprehensive scanning that catches vulnerabilities before they reach production",
    icon: <LockSimple size={32} weight="thin" className="group-hover:animate-subtle-float" />,
  },
  {
    title: "Ship Faster",
    description:
      "Cut your review cycles in half and ship features faster without compromising on quality",
    icon: <RocketLaunch size={32} weight="thin" className="group-hover:animate-subtle-float" />,
  },
  {
    title: "Team Insights",
    description:
      "Make data-driven decisions with automated reports and metrics that help your team continuously improve",
    icon: <ChartLineUp size={32} weight="thin" className="group-hover:animate-subtle-float" />,
  },
  {
    title: "Customized Rule-Based Reviews",
    description:
      "Create custom review rules that match your team's standards and let AI handle the heavy lifting",
    icon: <ClipboardText size={32} weight="thin" className="group-hover:animate-subtle-float" />,
  },
  {
    title: "PR Integration",
    description:
      "Works seamlessly with GitHub and GitLab - get instant feedback right where your team already works",
    icon: <ArrowsClockwise size={32} weight="thin" className="group-hover:animate-subtle-float" />,
  },
  {
    title: "Context-Aware Analysis",
    description:
      "Smart reviews that understand your codebase and team practices, giving you relevant suggestions that make sense",
    icon: <Brain size={32} weight="thin" className="group-hover:animate-subtle-float" />,
  },
  {
    title: "Multi-Language Support",
    description:
      "One tool for all your stack - supports 30+ languages so your entire team can use the same workflow",
    icon: <Globe size={32} weight="thin" className="group-hover:animate-subtle-float" />,
  },
];

export function Features() {
  return (
    <section id="features" className="relative py-32 overflow-hidden">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-semibold aurora-gradient">
            Designed for Modern Development Teams
          </h2>
          <p className="mt-4 text-lg text-white/70 max-w-2xl mx-auto">
            Everything you need to improve code quality and ship with confidence
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                variant="hover"
                className="p-8 h-full backdrop-blur-sm bg-white/[0.02] border border-white/[0.05] group"
              >
                <span className="relative block mb-4">
                  <span className="relative z-10 transform text-white/70 group-hover:text-white transition-colors duration-300">
                    {feature.icon}
                  </span>
                </span>
                <h3 className="text-lg font-medium bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">
                  {feature.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">
                  {feature.description}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
